/**
 * Stale Time Presets
 *
 * Use these to set consistent caching strategies across all hooks.
 * Even with staleTime, invalidateQueries() in mutations will
 * ALWAYS force a refetch — so data stays fresh after user actions.
 */

/** Data that almost never changes (languages, permissions, menus) */
export const STALE_FOREVER = { staleTime: Infinity, gcTime: Infinity } as const;

/** Admin CRUD data — only changes via explicit user actions (5 min) */
export const STALE_5_MIN = { staleTime: 5 * 60 * 1000 } as const;

/** User-facing data that can change externally (2 min) */
export const STALE_2_MIN = { staleTime: 2 * 60 * 1000 } as const;

/**
 * Query Key Factory
 *
 * Generates hierarchical query keys for a feature's CRUD operations.
 * React Query uses prefix matching for invalidation, so invalidating
 * `keys.all` will automatically invalidate all lists and details.
 */
export const createQueryKeys = (feature: string) => ({
  /** Invalidates everything for this feature */
  all: [feature] as const,

  /** Invalidates all lists regardless of filters */
  lists: () => [feature, "list"] as const,

  /** A specific filtered/paginated list */
  list: (filters?: object) => [feature, "list", filters] as const,

  /** Invalidates all detail views */
  details: () => [feature, "detail"] as const,

  /** A specific detail by ID */
  detail: (id: string | number) => [feature, "detail", id] as const,
});

// ==========================================
// Master Management Query Keys
// ==========================================
export const degreeKeys = createQueryKeys("degrees");
export const industryKeys = createQueryKeys("industries");
export const teamKeys = createQueryKeys("teams");
export const departmentKeys = createQueryKeys("departments");
export const categoryKeys = createQueryKeys("categories");
export const skillKeys = createQueryKeys("skills");

// ==========================================
// Profile Management Query Keys
// ==========================================
export const awardsKeys = createQueryKeys("awards");
export const certificationsKeys = createQueryKeys("certifications");
export const educationKeys = createQueryKeys("education");
export const experienceKeys = createQueryKeys("experience");
export const projectKeys = createQueryKeys("projects");
export const profileSkillKeys = createQueryKeys("profileSkills");
export const profileKeys = createQueryKeys("profile");
export const candidateKeys = createQueryKeys("candidates");
export const templateKeys = createQueryKeys("resumeTemplates");

// ==========================================
// Candidate Profile Section Query Keys
// ==========================================
export const candidateProfileKeys = {
  all: ["candidate-profile"] as const,
  detail: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "detail", String(id)] as const)
      : ([...candidateProfileKeys.all, "detail"] as const),
  skills: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "skills", String(id)] as const)
      : ([...candidateProfileKeys.all, "skills"] as const),
  projects: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "projects", String(id)] as const)
      : ([...candidateProfileKeys.all, "projects"] as const),
  education: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "education", String(id)] as const)
      : ([...candidateProfileKeys.all, "education"] as const),
  experiences: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "experiences", String(id)] as const)
      : ([...candidateProfileKeys.all, "experiences"] as const),
  certifications: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "certifications", String(id)] as const)
      : ([...candidateProfileKeys.all, "certifications"] as const),
  awards: (id?: number | string) =>
    id
      ? ([...candidateProfileKeys.all, "awards", String(id)] as const)
      : ([...candidateProfileKeys.all, "awards"] as const),
};

// ==========================================
// Auth Query Keys
// ==========================================
export const authKeys = {
  all: ["auth"] as const,
  me: () => ["auth", "me"] as const,
};
